import { format as formatWithDateFns } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import type { Locale as DateFnsLocale } from "date-fns";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import i18n from "./index.ts";
import { FALLBACK_LOCALE, type SupportedLocale } from "./resources.ts";

/**
 * pt-BR reads `dd/MM/yyyy`. The failure mode is silent: `03/04` is a valid date
 * under both orders and renders as a different day, so a call site that formats
 * dates by hand produces plausible wrong data rather than a visible bug. Route
 * dates through these helpers instead of hardcoding a pattern.
 */
type LocaleFormats = {
  dateFnsLocale: DateFnsLocale;
  dayjsLocale: string;
  /** date-fns tokens. */
  dateFns: { date: string; dateTime: string; time: string };
  /**
   * dayjs tokens, which are NOT date-fns tokens: dayjs reads `dd` as the short
   * weekday name and `yyyy` as a literal, so date-fns' `dd/MM/yyyy` silently
   * renders as `6ª/04/yyyy`. The two sets are kept apart deliberately.
   */
  dayjs: { date: string; dateTime: string; time: string };
};

const FORMATS: Record<SupportedLocale, LocaleFormats> = {
  en: {
    dateFnsLocale: enUS,
    dayjsLocale: "en",
    dateFns: {
      date: "MM/dd/yyyy",
      dateTime: "MM/dd/yyyy HH:mm",
      time: "HH:mm",
    },
    dayjs: {
      date: "MM/DD/YYYY",
      dateTime: "MM/DD/YYYY HH:mm",
      time: "HH:mm",
    },
  },
  "pt-BR": {
    dateFnsLocale: ptBR,
    dayjsLocale: "pt-br",
    dateFns: {
      date: "dd/MM/yyyy",
      dateTime: "dd/MM/yyyy HH:mm",
      time: "HH:mm",
    },
    dayjs: {
      date: "DD/MM/YYYY",
      dateTime: "DD/MM/YYYY HH:mm",
      time: "HH:mm",
    },
  },
};

/** The active language, narrowed to a locale we actually ship. */
export const activeLocale = (): SupportedLocale => {
  const resolved = i18n.resolvedLanguage ?? i18n.language;
  return resolved in FORMATS
    ? (resolved as SupportedLocale)
    : FALLBACK_LOCALE;
};

const activeFormats = (): LocaleFormats => FORMATS[activeLocale()];

/** date-fns `locale` option for the active language. */
export const dateFnsLocale = (): DateFnsLocale => activeFormats().dateFnsLocale;

/** dayjs locale name for the active language — `dayjs(d).locale(dayjsLocale())`. */
export const dayjsLocale = (): string => activeFormats().dayjsLocale;

/** pt-BR weeks start on Monday, en weeks on Sunday. */
export const weekStartsOn = (): 0 | 1 =>
  activeLocale() === "pt-BR" ? 1 : 0;

export const formatDate = (date: Date | number, pattern?: string): string =>
  formatWithDateFns(date, pattern ?? activeFormats().dateFns.date, {
    locale: dateFnsLocale(),
    weekStartsOn: weekStartsOn(),
  });

export const formatDateTime = (date: Date | number): string =>
  formatDate(date, activeFormats().dateFns.dateTime);

export const formatTime = (date: Date | number): string =>
  formatDate(date, activeFormats().dateFns.time);

/**
 * dayjs equivalent, for the call sites already built on dayjs. Any `pattern`
 * passed here must use dayjs tokens (`DD/MM/YYYY`), not date-fns ones.
 */
export const formatDayjs = (
  date: dayjs.ConfigType,
  pattern?: string,
): string =>
  dayjs(date)
    .locale(dayjsLocale())
    .format(pattern ?? activeFormats().dayjs.date);

export const formatDayjsDateTime = (date: dayjs.ConfigType): string =>
  formatDayjs(date, activeFormats().dayjs.dateTime);

/**
 * pt-BR groups with `.` and decimalises with `,` — `1.234,56`. Never build a
 * number string with `toFixed` + manual separators.
 */
export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions,
): string => new Intl.NumberFormat(activeLocale(), options).format(value);

export const formatPercent = (value: number, fractionDigits = 0): string =>
  formatNumber(value, {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
